/// <amd-module name="Dashboards/DashboardView/src/components/headers/DashboardPicker" />
import * as React from "react";
import { connect } from "react-redux";

import { DashboardViewAwareState } from "../../model/DashboardViewState";
import * as Selectors from "../../redux/selectors/Selectors";

import { Dashboard } from "Dashboards/Common/Contracts/Dashboard";
import { DashboardPickerProviderFactory, PickerDashboardItem } from "Dashboards/DashboardPickerProvider/DashboardPickerProviderFactory";

import { IProjectData } from "Tfs/Platform/Page";
import { ITfsPageDataAwareState } from "Tfs/ReduxCommon/Modules/TfsPageData/TfsPageDataContracts";
import { currentProject } from "Tfs/ReduxCommon/Modules/TfsPageData/TfsPageDataSelectors";

import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { DisableUserNamePrefixForProjectDashboardFeature } from "Dashboards/Common/Constants";
import { isFeatureEnabled } from "VSS/Platform/Feature";
import { IPickListSelection } from "VSSUI/PickList";
import { IVssBreadcrumbItem, IVssBreadcrumbItemInternal, VssBreadcrumb, VssBreadcrumbStyles } from "VSSUI/VssBreadcrumb";
import { DashboardOwner } from "../../model/Dashboard";

interface ConnectedDashboardPickerOwnProps {}
interface ConnectedDashboardPickerMappedProps {
    project: IProjectData;
    owner: DashboardOwner;
    dashboard: Dashboard;
    canCreateDashboards: boolean;
}

interface IDashboardPickerProps extends ConnectedDashboardPickerOwnProps, ConnectedDashboardPickerMappedProps {}

class DashboardPicker extends VssComponent<IDashboardPickerProps> {
    private providerFactory: DashboardPickerProviderFactory;

    public constructor(props: IDashboardPickerProps, context?: IVssComponentContext) {
        super(props, context);

        const pickerDashboard: PickerDashboardItem = {
            owner: this.props.owner,
            dashboard: this.props.dashboard
        };

        this.providerFactory = new DashboardPickerProviderFactory(this.context.pageContext, this.props.project, pickerDashboard);
    }

    public render(): JSX.Element | null {
        return (
            <VssBreadcrumb
                shouldUseFocusZone
                presentationStyles={VssBreadcrumbStyles.dividerLink}
                className="flex-noshrink"
                maxItemWidth="340px"
                pickListMaxWidth={400}
                pickListMinWidth={400}
                items={this.createBreadcrumbItems()}
            />
        );
    }

    private getDashboardName(): string {
        if (!this.props.dashboard.groupId && isFeatureEnabled(this.context.pageContext, DisableUserNamePrefixForProjectDashboardFeature, false)) {
            return this.props.dashboard.name;
        } else {
            return `${this.props.owner.name} - ${this.props.dashboard.name}`;
        }
    }

    private createBreadcrumbItems(): IVssBreadcrumbItem[] {
        const itemPicker = this.providerFactory.build(true /*showBrowseDashboards*/, this.props.canCreateDashboards);

        const breadcrumbItem: IVssBreadcrumbItemInternal = {
            key: "DashboardPicker",
            text: this.getDashboardName(),
            leftIconProps: {
                iconName: "ViewDashboard"
            },
            itemPicker,
            isHeaderPickerItem: true,
            role: "heading",
            ariaLevel: 1,

            // IVssBreadcrumbItem has a secret property, onSelectionChanged,
            // that we need to set to our picker provider function
            onSelectionChanged: (selection: IPickListSelection<PickerDashboardItem>) => {
                let preventDefault: boolean | void = false;
                if (itemPicker.onSelectedItemChanged && selection.selectedItems.length > 0) {
                    preventDefault = itemPicker.onSelectedItemChanged(selection.selectedItems[0]);
                }

                return preventDefault;
            }
        };

        return [breadcrumbItem];
    }
}

function mapStateToProps(state: DashboardViewAwareState & ITfsPageDataAwareState): ConnectedDashboardPickerMappedProps {
    return {
        dashboard: Selectors.packServerDashboard(state),
        project: currentProject(state)!,
        owner: Selectors.getDashboardOwner(state)!,
        canCreateDashboards: Selectors.canCreateDashboards(state)
    };
}

export const ConnectedDashboardPicker = connect(mapStateToProps)(DashboardPicker);
